import { useState, useEffect, useCallback } from 'react';
import PageHeader from '../../components/PageHeader';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Plus,
  Search,
  Briefcase,
  Building2,
  Users,
  MoreHorizontal,
  CheckCircle2,
  Clock,
  Mail,
  RefreshCw,
  Loader2,
  Copy,
  ExternalLink,
  X,
  Phone,
  Globe,
  MapPin,
  FileText,
  Edit3,
  ChevronRight,
  Filter,
  UserCheck,
  UserX,
  MessageSquare,
  Target,
  UserPlus,
  ShieldCheck,
  XCircle,
  AlertTriangle,
  Trash2,
  Sparkles
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Stage configuration for consultant pipeline
const STAGE_CONFIG = {
  PENDING_TRIAL: { label: 'Pending Trial', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock },
  LEAD: { label: 'Lead', color: 'bg-purple-100 text-purple-700 border-purple-200', icon: MessageSquare },
  DISCOVERY_CALL: { label: 'Discovery', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Phone },
  VETTING: { label: 'Vetting', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Target },
  ONBOARDING: { label: 'Onboarding', color: 'bg-teal-100 text-teal-700 border-teal-200', icon: Clock },
  ACTIVE: { label: 'Active', color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
  INACTIVE: { label: 'Inactive', color: 'bg-slate-100 text-slate-600 border-slate-200', icon: UserX },
};

export default function ConsultantManagement() {
  const { authToken } = useAuth();
  
  // Data state
  const [consultants, setConsultants] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, pending: 0, clients_served: 0, stage_counts: {} });
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  
  // Modal state
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [createResult, setCreateResult] = useState(null);
  
  // Quick View state
  const [selectedConsultant, setSelectedConsultant] = useState(null);
  const [consultantDetail, setConsultantDetail] = useState(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);

  // Pending-trial approval state (used when drawer shows a PENDING_TRIAL row)
  const [approveResult, setApproveResult] = useState(null);
  const [isRejecting, setIsRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  // Manual Add Trial Request modal state
  const [isManualTrialOpen, setIsManualTrialOpen] = useState(false);
  const [manualTrialResult, setManualTrialResult] = useState(null);
  const [manualTrialForm, setManualTrialForm] = useState({
    request_type: 'CONSULTANCY',
    company_name: '',
    contact_first_name: '',
    contact_last_name: '',
    contact_email: '',
    contact_phone: '',
    contact_title: '',
    requested_plan: 'GROWTH',
    message: '',
  });
  
  // Edit modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  
  // Note modal state
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [noteText, setNoteText] = useState('');

  // Purge modal state
  const [isPurgeOpen, setIsPurgeOpen] = useState(false);
  
  // Form state
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    specialized_sector: ''
  });

  const fetchData = useCallback(async () => {
    if (!authToken) return;
    setIsLoading(true);
    
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      
      const [consultantsRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/consultants?${params}`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        }),
        fetch(`${API_URL}/api/admin/consultants/stats`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        })
      ]);
      
      if (consultantsRes.ok) {
        const data = await consultantsRes.json();
        setConsultants(data);
      }
      
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching consultants:', error);
    } finally {
      setIsLoading(false);
    }
  }, [authToken, searchQuery]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch consultant detail for Quick View
  const fetchConsultantDetail = async (consultantId) => {
    setIsLoadingDetail(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/consultants/${consultantId}`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setConsultantDetail(data);
      }
    } catch (error) {
      console.error('Error fetching consultant detail:', error);
    } finally {
      setIsLoadingDetail(false);
    }
  };

  // Handle row click to open Quick View
  const handleRowClick = (consultant) => {
    setSelectedConsultant(consultant);
    setApproveResult(null);
    setRejectReason('');
    // PENDING_TRIAL rows aren't real consultant users yet — they live in
    // `trial_requests`. Skip the consultant detail fetch and render the
    // pending-trial approval view in the drawer instead.
    if (consultant.stage === 'PENDING_TRIAL') {
      setConsultantDetail(null);
      return;
    }
    fetchConsultantDetail(consultant.id);
  };

  // Close Quick View
  const closeQuickView = () => {
    setSelectedConsultant(null);
    setConsultantDetail(null);
    setApproveResult(null);
    setRejectReason('');
  };

  // Approve a pending trial request (consultancy signup)
  const handleApproveTrial = async () => {
    if (!selectedConsultant) return;
    setIsProcessing(true);
    try {
      const response = await fetch(
        `${API_URL}/api/trial/requests/${selectedConsultant.id}/approve`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`,
          },
          body: JSON.stringify({ trial_days: 14, send_welcome_email: false }),
        }
      );
      if (response.ok) {
        const data = await response.json();
        setApproveResult(data);
        fetchData();
      } else {
        const err = await response.json().catch(() => ({}));
        alert(`Approval failed: ${err.detail || response.statusText}`);
      }
    } catch (error) {
      console.error('Error approving trial:', error);
      alert('Failed to approve trial request');
    } finally {
      setIsProcessing(false);
    }
  };

  // Reject a pending trial request
  const handleRejectTrial = async () => {
    if (!selectedConsultant) return;
    setIsProcessing(true);
    try {
      const response = await fetch(
        `${API_URL}/api/trial/requests/${selectedConsultant.id}/reject`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`,
          },
          body: JSON.stringify({ reason: rejectReason || null }),
        }
      );
      if (response.ok) {
        setIsRejecting(false);
        setRejectReason('');
        closeQuickView();
        fetchData();
      } else {
        const err = await response.json().catch(() => ({}));
        alert(`Reject failed: ${err.detail || response.statusText}`);
      }
    } catch (error) {
      console.error('Error rejecting trial:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Submit manual trial request (Super Admin manually adds a lead)
  const handleSubmitManualTrial = async () => {
    if (
      !manualTrialForm.company_name ||
      !manualTrialForm.contact_first_name ||
      !manualTrialForm.contact_last_name ||
      !manualTrialForm.contact_email
    ) {
      return;
    }
    setIsProcessing(true);
    try {
      const response = await fetch(`${API_URL}/api/trial/requests/manual`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify(manualTrialForm),
      });
      if (response.ok) {
        const data = await response.json();
        setManualTrialResult(data);
        fetchData();
        // Auto-close after a brief confirmation so the user sees the success
        // state but isn't stuck on a "Done" button.
        setTimeout(() => closeManualTrialModal(), 1500);
      } else {
        const err = await response.json().catch(() => ({}));
        alert(`Failed: ${err.detail || response.statusText}`);
      }
    } catch (error) {
      console.error('Error creating manual trial request:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const closeManualTrialModal = () => {
    setIsManualTrialOpen(false);
    setTimeout(() => {
      setManualTrialResult(null);
      setManualTrialForm({
        request_type: 'CONSULTANCY',
        company_name: '',
        contact_first_name: '',
        contact_last_name: '',
        contact_email: '',
        contact_phone: '',
        contact_title: '',
        requested_plan: 'GROWTH',
        message: '',
      });
    }, 300);
  };

  // Toggle founding-member status for an org. Used from the row
  // dropdown in the table — stays on the same screen, just refetches.
  const toggleFoundingMember = async (organizationId, currentValue) => {
    try {
      const response = await fetch(
        `${API_URL}/api/billing/founding-member/toggle`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`,
          },
          body: JSON.stringify({
            organization_id: organizationId,
            is_founding_member: !currentValue,
          }),
        }
      );
      if (response.ok) {
        fetchData();
      } else {
        const err = await response.json().catch(() => ({}));
        alert(`Toggle failed: ${err.detail || response.statusText}`);
      }
    } catch (error) {
      console.error('Founding member toggle failed:', error);
    }
  };

  // Open edit modal
  const openEditModal = () => {
    if (consultantDetail) {
      setEditForm({
        phone: consultantDetail.phone || '',
        title: consultantDetail.title || '',
        uei: consultantDetail.uei || '',
        cage_code: consultantDetail.cage_code || '',
        website: consultantDetail.website || '',
        specialized_sector: consultantDetail.specialized_sector || '',
        stage: consultantDetail.stage || 'ACTIVE',
        bio: consultantDetail.bio || '',
        years_experience: consultantDetail.years_experience || '',
        recruitment_source: consultantDetail.recruitment_source || '',
        address_line1: consultantDetail.address?.line1 || '',
        address_city: consultantDetail.address?.city || '',
        address_state: consultantDetail.address?.state || '',
        address_zip: consultantDetail.address?.zip_code || '',
      });
      setIsEditModalOpen(true);
    }
  };

  // Save edit
  const handleSaveEdit = async () => {
    setIsProcessing(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/consultants/${selectedConsultant.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          phone: editForm.phone || null,
          title: editForm.title || null,
          uei: editForm.uei || null,
          cage_code: editForm.cage_code || null,
          website: editForm.website || null,
          specialized_sector: editForm.specialized_sector || null,
          stage: editForm.stage || null,
          bio: editForm.bio || null,
          years_experience: editForm.years_experience ? parseInt(editForm.years_experience) : null,
          recruitment_source: editForm.recruitment_source || null,
          address: editForm.address_line1 ? {
            line1: editForm.address_line1,
            city: editForm.address_city,
            state: editForm.address_state,
            zip_code: editForm.address_zip,
            country: 'USA'
          } : null
        })
      });
      
      if (response.ok) {
        setIsEditModalOpen(false);
        fetchConsultantDetail(selectedConsultant.id);
        fetchData();
      }
    } catch (error) {
      console.error('Error updating consultant:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Open note modal
  const openNoteModal = () => {
    setNoteText(consultantDetail?.internal_notes || '');
    setIsNoteModalOpen(true);
  };

  // Save note
  const handleSaveNote = async () => {
    setIsProcessing(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/consultants/${selectedConsultant.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ internal_notes: noteText })
      });
      
      if (response.ok) {
        setIsNoteModalOpen(false);
        fetchConsultantDetail(selectedConsultant.id);
      }
    } catch (error) {
      console.error('Error saving note:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Purge consultant (permanent hard-delete)
  const handlePurgeConsultant = async () => {
    setIsProcessing(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/consultants/${selectedConsultant.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (response.ok) {
        setIsPurgeOpen(false);
        closeQuickView();
        fetchData();
      } else {
        const err = await response.json().catch(() => ({}));
        alert(`Error: ${err.detail || response.statusText}`);
      }
    } catch (error) {
      console.error('Error purging consultant:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Promote stage
  const handlePromoteStage = async (newStage) => {
    setIsProcessing(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/consultants/${selectedConsultant.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ stage: newStage })
      });
      
      if (response.ok) {
        fetchConsultantDetail(selectedConsultant.id);
        fetchData();
      }
    } catch (error) {
      console.error('Error promoting stage:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAddConsultant = async () => {
    if (!form.full_name || !form.email) return;
    setIsProcessing(true);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/consultants`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(form)
      });
      
      if (response.ok) {
        const data = await response.json();
        setCreateResult(data);
        fetchData();
      } else {
        const err = await response.json();
        alert(`Error: ${err.detail}`);
      }
    } catch (error) {
      console.error('Error creating consultant:', error);
      alert('Failed to create consultant');
    } finally {
      setIsProcessing(false);
    }
  };

  const resetForm = () => {
    setForm({ full_name: '', email: '', specialized_sector: '' });
    setCreateResult(null);
  };

  const closeModal = () => {
    setIsAddModalOpen(false);
    setTimeout(resetForm, 300);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Filter consultants by stage
  const filteredConsultants = consultants.filter(c => {
    if (stageFilter === 'all') return true;
    return c.stage === stageFilter;
  });

  // Get next stage for promotion
  const getNextStage = (currentStage) => {
    const stages = ['LEAD', 'DISCOVERY_CALL', 'VETTING', 'ONBOARDING', 'ACTIVE'];
    const currentIndex = stages.indexOf(currentStage);
    if (currentIndex < stages.length - 1) {
      return stages[currentIndex + 1];
    }
    return null;
  };

  const statCards = [
    { label: 'Total Consultants', value: stats.total, icon: Briefcase, color: 'text-teal-600', bgColor: 'bg-teal-50' },
    { label: 'Active (30d)', value: stats.active, icon: CheckCircle2, color: 'text-emerald-600', bgColor: 'bg-emerald-50' },
    { label: 'In Pipeline', value: stats.pending, icon: Clock, color: 'text-amber-600', bgColor: 'bg-amber-50' },
    { label: 'Contractors Served', value: stats.clients_served, icon: Building2, color: 'text-blue-600', bgColor: 'bg-blue-50' },
  ];

  // Stage filter tabs
  const stageTabs = [
    { key: 'all', label: 'All', count: consultants.length },
    { key: 'LEAD', label: 'Lead', count: stats.stage_counts?.LEAD || 0 },
    { key: 'DISCOVERY_CALL', label: 'Discovery', count: stats.stage_counts?.DISCOVERY_CALL || 0 },
    { key: 'VETTING', label: 'Vetting', count: stats.stage_counts?.VETTING || 0 },
    { key: 'ONBOARDING', label: 'Onboarding', count: stats.stage_counts?.ONBOARDING || 0 },
    { key: 'ACTIVE', label: 'Active', count: stats.stage_counts?.ACTIVE || 0 },
    { key: 'INACTIVE', label: 'Inactive', count: stats.stage_counts?.INACTIVE || 0 },
  ];

  return (
    <div data-testid="consultant-management" className="flex">
      {/* Main Content */}
      <div className={cn("flex-1 transition-all duration-300", selectedConsultant ? "mr-[420px]" : "")}>
        <PageHeader 
          title="Consultant Management"
          description="Manage consultants who serve your contractor organizations"
          action={
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={fetchData}
                disabled={isLoading}
                className="border-slate-200"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button
                variant="outline"
                onClick={() => setIsManualTrialOpen(true)}
                className="border-amber-200 text-amber-700 hover:bg-amber-50"
                data-testid="add-trial-request-btn"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Add Trial Request
              </Button>
              <Button 
                className="bg-teal-700 hover:bg-teal-800 text-white"
                onClick={() => setIsAddModalOpen(true)}
                data-testid="add-consultant-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Consultant
              </Button>
            </div>
          }
        />
        
        <div className="p-8 space-y-6">
          {/* Stats cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {statCards.map((stat, index) => (
              <Card key={stat.label || `stat-${index}`} className="border-slate-200 shadow-sm">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500 mb-1">{stat.label}</p>
                      <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {stat.value}
                      </p>
                    </div>
                    <div className={`p-3 ${stat.bgColor} rounded-lg`}>
                      <stat.icon className={`w-5 h-5 ${stat.color}`} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Stage Filter Tabs */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2">
            <Filter className="w-4 h-4 text-slate-400 flex-shrink-0" />
            {stageTabs.map((tab) => (
              <Button
                key={tab.key}
                variant={stageFilter === tab.key ? "default" : "outline"}
                size="sm"
                onClick={() => setStageFilter(tab.key)}
                className={cn(
                  "flex-shrink-0",
                  stageFilter === tab.key 
                    ? "bg-teal-700 hover:bg-teal-800 text-white" 
                    : "border-slate-200"
                )}
              >
                {tab.label}
                <Badge variant="secondary" className="ml-2 bg-white/20">
                  {tab.count}
                </Badge>
              </Button>
            ))}
          </div>

          {/* Search */}
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input 
              placeholder="Search consultants..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 border-slate-200"
              data-testid="consultant-search"
            />
          </div>

          {/* Consultant list */}
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-0">
              {/* Table header */}
              <div className="grid grid-cols-8 gap-4 px-6 py-3 bg-slate-50 border-b border-slate-200 text-xs font-medium text-slate-500 uppercase tracking-wider">
                <div className="col-span-2">Consultant</div>
                <div>Organization</div>
                <div>Stage</div>
                <div>Sector</div>
                <div>Contractors</div>
                <div>Status</div>
                <div>Actions</div>
              </div>
              
              {isLoading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
                </div>
              ) : filteredConsultants.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                    <Briefcase className="w-8 h-8 text-slate-400" />
                  </div>
                  <p className="text-slate-500 text-sm mb-1">
                    {searchQuery || stageFilter !== 'all' ? 'No consultants match your filters' : 'No consultants registered yet'}
                  </p>
                  <p className="text-slate-400 text-xs mb-4">
                    {stageFilter !== 'all' ? 'Try a different stage filter' : 'Add your first consultant to get started'}
                  </p>
                  {stageFilter === 'all' && (
                    <Button 
                      onClick={() => setIsAddModalOpen(true)}
                      className="bg-teal-700 hover:bg-teal-800 text-white"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Consultant
                    </Button>
                  )}
                </div>
              ) : (
                filteredConsultants.map((consultant) => {
                  const stageConfig = STAGE_CONFIG[consultant.stage] || STAGE_CONFIG.ACTIVE;
                  const StageIcon = stageConfig.icon;
                  
                  return (
                    <div 
                      key={consultant.id} 
                      className={cn(
                        "grid grid-cols-8 gap-4 px-6 py-4 border-b border-slate-100 hover:bg-slate-50 transition-colors cursor-pointer",
                        selectedConsultant?.id === consultant.id && "bg-teal-50 hover:bg-teal-50"
                      )}
                      onClick={() => handleRowClick(consultant)}
                      data-testid={`consultant-row-${consultant.id}`}
                    >
                      <div className="col-span-2 flex items-center gap-3">
                        <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center">
                          <Briefcase className="w-5 h-5 text-teal-700" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{consultant.full_name}</p>
                          <p className="text-xs text-slate-500">{consultant.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center text-sm text-slate-600">
                        <span className="truncate">{consultant.organization_name}</span>
                        {consultant.is_founding_member && (
                          <span
                            className="ml-2 flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700 border border-amber-200"
                            title="Founding Member — 20% off for 12 months"
                            data-testid={`founding-badge-${consultant.id}`}
                          >
                            <Sparkles className="w-3 h-3 mr-0.5" /> FM
                          </span>
                        )}
                      </div>
                      <div className="flex items-center">
                        <Badge className={cn("border text-xs", stageConfig.color)}>
                          <StageIcon className="w-3 h-3 mr-1" />
                          {stageConfig.label}
                        </Badge>
                      </div>
                      <div className="flex items-center">
                        {consultant.specialized_sector ? (
                          <Badge variant="secondary" className="text-xs">
                            {consultant.specialized_sector}
                          </Badge>
                        ) : (
                          <span className="text-slate-400 text-sm">-</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1 text-sm text-slate-600">
                        <Building2 className="w-4 h-4 text-slate-400" />
                        {consultant.client_count}
                      </div>
                      <div className="flex items-center">
                        <Badge className={consultant.status === 'Active' 
                          ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' 
                          : 'bg-slate-100 text-slate-600'
                        }>
                          {consultant.status}
                        </Badge>
                      </div>
                      <div className="flex items-center">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); copyToClipboard(consultant.email); }}>
                              <Mail className="w-4 h-4 mr-2" />
                              Copy Email
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); copyToClipboard(consultant.organization_id); }}>
                              <Copy className="w-4 h-4 mr-2" />
                              Copy Org ID
                            </DropdownMenuItem>
                            {consultant.stage !== 'PENDING_TRIAL' && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={(e) => { e.stopPropagation(); toggleFoundingMember(consultant.organization_id, consultant.is_founding_member); }}
                                  data-testid={`toggle-founding-${consultant.id}`}
                                >
                                  <Sparkles className={cn(
                                    "w-4 h-4 mr-2",
                                    consultant.is_founding_member ? "text-amber-500" : "text-slate-400",
                                  )} />
                                  {consultant.is_founding_member ? 'Remove Founding Status' : 'Mark as Founding Member'}
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  );
                })
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* QUICK VIEW PANEL */}
      {selectedConsultant && (
        <div className="fixed right-0 top-0 h-full w-[420px] bg-slate-50 border-l border-slate-200 shadow-xl z-40 overflow-y-auto" data-testid="consultant-quick-view">
          {/* Panel Header */}
          <div className="bg-teal-700 px-6 py-4 text-white">
            <div className="flex items-center justify-between mb-4">
              <p className="text-xs font-semibold text-teal-200 uppercase tracking-wider">Consultant Profile</p>
              <button 
                onClick={closeQuickView}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-teal-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-teal-600 rounded-xl flex items-center justify-center">
                <Briefcase className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-semibold">{selectedConsultant.full_name}</h3>
                <p className="text-sm text-teal-200">{selectedConsultant.organization_name}</p>
              </div>
            </div>
          </div>

          {isLoadingDetail ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
            </div>
          ) : selectedConsultant?.stage === 'PENDING_TRIAL' ? (
            // -------- PENDING TRIAL APPROVAL VIEW --------
            <div className="p-6 space-y-5" data-testid="pending-trial-drawer">
              {approveResult ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                    <div className="text-sm">
                      <p className="font-semibold text-emerald-900">Approved</p>
                      <p className="text-emerald-700 text-xs">{approveResult.message}</p>
                    </div>
                  </div>

                  <div className="space-y-2 p-4 bg-white border border-slate-200 rounded-lg">
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider">Workspace</p>
                      <p className="text-sm font-medium text-slate-900">{approveResult.organization_name}</p>
                      <p className="text-xs text-slate-500 font-mono">/{approveResult.organization_slug}</p>
                    </div>
                    <div className="pt-2 border-t border-slate-100">
                      <p className="text-xs text-slate-500 uppercase tracking-wider">Admin Email</p>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-teal-700">{approveResult.admin_email}</p>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0"
                                onClick={() => copyToClipboard(approveResult.admin_email)}>
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                    <div className="pt-2 border-t border-slate-100">
                      <p className="text-xs text-slate-500 uppercase tracking-wider">Temporary Password</p>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-mono font-medium text-amber-700 break-all">{approveResult.temporary_password}</p>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 flex-shrink-0"
                                onClick={() => copyToClipboard(approveResult.temporary_password)}>
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                      <p className="text-xs text-amber-600 mt-1">
                        Share this with the customer manually. The welcome email is not yet wired up.
                      </p>
                    </div>
                    <div className="pt-2 border-t border-slate-100">
                      <p className="text-xs text-slate-500 uppercase tracking-wider">Login URL</p>
                      <a href={approveResult.invite_url} target="_blank" rel="noopener noreferrer"
                         className="text-sm text-teal-600 hover:underline break-all">
                        {approveResult.invite_url}
                      </a>
                    </div>
                  </div>

                  <Button onClick={closeQuickView} className="w-full bg-teal-700 hover:bg-teal-800 text-white">
                    Done
                  </Button>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <Clock className="w-5 h-5 text-amber-600 flex-shrink-0" />
                    <div className="text-sm">
                      <p className="font-semibold text-amber-900">Pending Approval</p>
                      <p className="text-amber-700 text-xs">
                        This consultancy submitted a trial request. Approve to provision their workspace and admin login.
                      </p>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold text-slate-900 mb-3">Submitted Information</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-start gap-2">
                        <Mail className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-700 break-all">{selectedConsultant.email}</span>
                      </div>
                      {selectedConsultant.phone && (
                        <div className="flex items-center gap-2">
                          <Phone className="w-4 h-4 text-slate-400" />
                          <span className="text-slate-700">{selectedConsultant.phone}</span>
                        </div>
                      )}
                      {selectedConsultant.title && (
                        <div className="flex items-center gap-2">
                          <Briefcase className="w-4 h-4 text-slate-400" />
                          <span className="text-slate-700">{selectedConsultant.title}</span>
                        </div>
                      )}
                      {selectedConsultant.website && (
                        <div className="flex items-center gap-2">
                          <Globe className="w-4 h-4 text-slate-400" />
                          <span className="text-slate-700">{selectedConsultant.website}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {selectedConsultant.internal_notes && (
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-2">Message</h4>
                      <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 text-sm text-slate-700 whitespace-pre-wrap">
                        {selectedConsultant.internal_notes}
                      </div>
                    </div>
                  )}

                  <div>
                    <h4 className="font-semibold text-slate-900 mb-2">Submitted</h4>
                    <p className="text-sm text-slate-600">{formatDate(selectedConsultant.created_at)}</p>
                  </div>

                  {isRejecting ? (
                    <div className="space-y-3 pt-4 border-t border-slate-200">
                      <Label className="text-slate-700">Reason for rejection (optional)</Label>
                      <Textarea
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        placeholder="Internal note — why this request is being rejected..."
                        rows={3}
                        data-testid="reject-trial-reason"
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <Button
                          variant="outline"
                          onClick={() => { setIsRejecting(false); setRejectReason(''); }}
                          disabled={isProcessing}
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={handleRejectTrial}
                          disabled={isProcessing}
                          className="bg-rose-600 hover:bg-rose-700 text-white"
                          data-testid="confirm-reject-trial-btn"
                        >
                          {isProcessing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <XCircle className="w-4 h-4 mr-2" />}
                          Confirm Reject
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3 pt-4 border-t border-slate-200">
                      <Button
                        onClick={handleApproveTrial}
                        disabled={isProcessing}
                        className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid="approve-trial-btn"
                      >
                        {isProcessing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
                        Approve & Provision Workspace
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setIsRejecting(true)}
                        disabled={isProcessing}
                        className="w-full border-rose-200 text-rose-700 hover:bg-rose-50"
                        data-testid="reject-trial-btn"
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Reject Request
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          ) : consultantDetail ? (
            <div className="p-6 space-y-6">
              {/* Stage & Status */}
              <div>
                <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <Target className="w-4 h-4 text-teal-600" />
                  Pipeline Stage
                </h4>
                <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200">
                  <Badge className={cn("border", STAGE_CONFIG[consultantDetail.stage]?.color || STAGE_CONFIG.ACTIVE.color)}>
                    {STAGE_CONFIG[consultantDetail.stage]?.label || 'Active'}
                  </Badge>
                  {getNextStage(consultantDetail.stage) && (
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handlePromoteStage(getNextStage(consultantDetail.stage))}
                      disabled={isProcessing}
                      className="text-xs"
                    >
                      <ChevronRight className="w-3 h-3 mr-1" />
                      Promote to {STAGE_CONFIG[getNextStage(consultantDetail.stage)]?.label}
                    </Button>
                  )}
                </div>
              </div>

              {/* Contact Info */}
              <div>
                <h4 className="font-semibold text-slate-900 mb-3">Contact Information</h4>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-700">{consultantDetail.email}</span>
                  </div>
                  {consultantDetail.phone && (
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="w-4 h-4 text-slate-400" />
                      <span className="text-slate-700">{consultantDetail.phone}</span>
                    </div>
                  )}
                  {consultantDetail.website && (
                    <div className="flex items-center gap-2 text-sm">
                      <Globe className="w-4 h-4 text-slate-400" />
                      <a href={consultantDetail.website.startsWith('http') ? consultantDetail.website : `https://${consultantDetail.website}`} 
                         target="_blank" rel="noopener noreferrer" 
                         className="text-teal-600 hover:underline">
                        {consultantDetail.website}
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {/* Business Identity */}
              <div>
                <h4 className="font-semibold text-slate-900 mb-3">Business Identity</h4>
                <div className="space-y-2">
                  {consultantDetail.uei && (
                    <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200">
                      <span className="text-sm text-slate-500">UEI</span>
                      <span className="font-mono font-medium text-slate-900">{consultantDetail.uei}</span>
                    </div>
                  )}
                  {consultantDetail.cage_code && (
                    <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200">
                      <span className="text-sm text-slate-500">CAGE Code</span>
                      <span className="font-mono font-medium text-slate-900">{consultantDetail.cage_code}</span>
                    </div>
                  )}
                  {consultantDetail.specialized_sector && (
                    <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200">
                      <span className="text-sm text-slate-500">Sector</span>
                      <Badge variant="secondary">{consultantDetail.specialized_sector}</Badge>
                    </div>
                  )}
                  {!consultantDetail.uei && !consultantDetail.cage_code && !consultantDetail.specialized_sector && (
                    <p className="text-sm text-slate-400 italic">No business identity info yet</p>
                  )}
                </div>
              </div>

              {/* Address */}
              {consultantDetail.address?.line1 && (
                <div>
                  <h4 className="font-semibold text-slate-900 mb-3">Address</h4>
                  <div className="flex items-start gap-2 text-sm text-slate-600">
                    <MapPin className="w-4 h-4 text-slate-400 mt-0.5" />
                    <div>
                      <p>{consultantDetail.address.line1}</p>
                      {consultantDetail.address.city && (
                        <p>{consultantDetail.address.city}, {consultantDetail.address.state} {consultantDetail.address.zip_code}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Stats */}
              <div>
                <h4 className="font-semibold text-slate-900 mb-3">Performance</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-white rounded-lg border border-slate-200 text-center">
                    <p className="text-2xl font-bold text-teal-700">{consultantDetail.client_count}</p>
                    <p className="text-xs text-slate-500">Clients</p>
                  </div>
                  <div className="p-3 bg-white rounded-lg border border-slate-200 text-center">
                    <p className="text-2xl font-bold text-blue-700">{consultantDetail.active_pursuits}</p>
                    <p className="text-xs text-slate-500">Active Pursuits</p>
                  </div>
                </div>
              </div>

              {/* Internal Notes */}
              {consultantDetail.internal_notes && (
                <div>
                  <h4 className="font-semibold text-slate-900 mb-3">Internal Notes</h4>
                  <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 text-sm text-slate-700">
                    {consultantDetail.internal_notes}
                  </div>
                </div>
              )}

              {/* Timestamps */}
              <div className="text-xs text-slate-400 space-y-1">
                <p>Created: {formatDate(consultantDetail.created_at)}</p>
                <p>Last Login: {formatDate(consultantDetail.last_login_at)}</p>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3 pt-4 border-t border-slate-200">
                <Button
                  className="w-full bg-teal-700 hover:bg-teal-800 text-white"
                  onClick={openEditModal}
                >
                  <Edit3 className="w-4 h-4 mr-2" />
                  Edit Profile
                </Button>
                <Button
                  variant="outline"
                  className="w-full border-slate-200"
                  onClick={openNoteModal}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  {consultantDetail.internal_notes ? 'Edit Note' : 'Add Note'}
                </Button>
                <Button
                  variant="outline"
                  className="w-full border-red-300 text-red-700 hover:bg-red-50"
                  onClick={() => setIsPurgeOpen(true)}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Purge Consultant
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      )}

      {/* ADD CONSULTANT MODAL */}
      <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
        <DialogContent className="sm:max-w-md" data-testid="add-consultant-modal">
          {createResult ? (
            <div className="py-6 text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Consultant Created!
              </h3>
              <p className="text-slate-600 mb-4">
                {createResult.full_name} has been added as a consultant.
              </p>
              
              <div className="bg-slate-50 rounded-lg p-4 text-left mb-6 space-y-2">
                <div>
                  <p className="text-xs text-slate-500 uppercase">Organization</p>
                  <p className="text-sm font-medium text-slate-900">{createResult.organization_name}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase">Email</p>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-teal-700">{createResult.email}</p>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={() => copyToClipboard(createResult.email)}
                    >
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
                <div className="pt-2 border-t border-slate-200">
                  <p className="text-xs text-amber-600">
                    A temporary password has been generated. Check server logs for credentials.
                  </p>
                </div>
              </div>
              
              <Button onClick={closeModal} className="bg-teal-700 hover:bg-teal-800 text-white">
                Done
              </Button>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-teal-600" />
                  Add Consultant
                </DialogTitle>
                <DialogDescription>
                  Create a new consultant account. This will also create their consultancy organization.
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="full_name">Full Name *</Label>
                  <Input
                    id="full_name"
                    placeholder="John Smith"
                    value={form.full_name}
                    onChange={(e) => setForm({...form, full_name: e.target.value})}
                    data-testid="consultant-name-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="john@consulting.com"
                    value={form.email}
                    onChange={(e) => setForm({...form, email: e.target.value})}
                    data-testid="consultant-email-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="sector">Specialized Sector (Optional)</Label>
                  <Input
                    id="sector"
                    placeholder="e.g., IT Services, Defense, Healthcare"
                    value={form.specialized_sector}
                    onChange={(e) => setForm({...form, specialized_sector: e.target.value})}
                    data-testid="consultant-sector-input"
                  />
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={closeModal}>
                  Cancel
                </Button>
                <Button
                  onClick={handleAddConsultant}
                  disabled={!form.full_name || !form.email || isProcessing}
                  className="bg-teal-700 hover:bg-teal-800 text-white"
                  data-testid="submit-consultant-btn"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Create Consultant
                    </>
                  )}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* EDIT PROFILE MODAL */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-consultant-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit3 className="w-5 h-5 text-teal-600" />
              Edit Consultant Profile
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Stage */}
            <div className="space-y-2">
              <Label>Pipeline Stage</Label>
              <Select value={editForm.stage} onValueChange={(v) => setEditForm({...editForm, stage: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="LEAD">Lead</SelectItem>
                  <SelectItem value="DISCOVERY_CALL">Discovery Call</SelectItem>
                  <SelectItem value="VETTING">Vetting</SelectItem>
                  <SelectItem value="ONBOARDING">Onboarding</SelectItem>
                  <SelectItem value="ACTIVE">Active</SelectItem>
                  <SelectItem value="INACTIVE">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Contact */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={editForm.phone}
                  onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                  placeholder="(555) 123-4567"
                />
              </div>
              <div className="space-y-2">
                <Label>Title</Label>
                <Input
                  value={editForm.title}
                  onChange={(e) => setEditForm({...editForm, title: e.target.value})}
                  placeholder="Managing Partner"
                />
              </div>
            </div>

            {/* Business Identity */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>UEI</Label>
                <Input
                  value={editForm.uei}
                  onChange={(e) => setEditForm({...editForm, uei: e.target.value})}
                  placeholder="H93L94K2M1P5"
                  maxLength={12}
                  className="font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label>CAGE Code</Label>
                <Input
                  value={editForm.cage_code}
                  onChange={(e) => setEditForm({...editForm, cage_code: e.target.value})}
                  placeholder="7Z9X2"
                  maxLength={5}
                  className="font-mono"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Website</Label>
              <Input
                value={editForm.website}
                onChange={(e) => setEditForm({...editForm, website: e.target.value})}
                placeholder="www.consulting-firm.com"
              />
            </div>

            <div className="space-y-2">
              <Label>Specialized Sector</Label>
              <Input
                value={editForm.specialized_sector}
                onChange={(e) => setEditForm({...editForm, specialized_sector: e.target.value})}
                placeholder="IT Services, Defense"
              />
            </div>

            {/* Address */}
            <div className="space-y-2">
              <Label>Address</Label>
              <Input
                value={editForm.address_line1}
                onChange={(e) => setEditForm({...editForm, address_line1: e.target.value})}
                placeholder="123 Main Street"
                className="mb-2"
              />
              <div className="grid grid-cols-3 gap-2">
                <Input
                  value={editForm.address_city}
                  onChange={(e) => setEditForm({...editForm, address_city: e.target.value})}
                  placeholder="City"
                />
                <Input
                  value={editForm.address_state}
                  onChange={(e) => setEditForm({...editForm, address_state: e.target.value})}
                  placeholder="State"
                  maxLength={2}
                />
                <Input
                  value={editForm.address_zip}
                  onChange={(e) => setEditForm({...editForm, address_zip: e.target.value})}
                  placeholder="ZIP"
                />
              </div>
            </div>

            {/* Profile fields */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Years Experience</Label>
                <Input
                  type="number"
                  value={editForm.years_experience}
                  onChange={(e) => setEditForm({...editForm, years_experience: e.target.value})}
                  placeholder="10"
                />
              </div>
              <div className="space-y-2">
                <Label>Recruitment Source</Label>
                <Input
                  value={editForm.recruitment_source}
                  onChange={(e) => setEditForm({...editForm, recruitment_source: e.target.value})}
                  placeholder="Referral, LinkedIn"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Bio</Label>
              <Textarea
                value={editForm.bio}
                onChange={(e) => setEditForm({...editForm, bio: e.target.value})}
                placeholder="Brief professional background..."
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveEdit}
              disabled={isProcessing}
              className="bg-teal-700 hover:bg-teal-800 text-white"
            >
              {isProcessing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* NOTE MODAL */}
      <Dialog open={isNoteModalOpen} onOpenChange={setIsNoteModalOpen}>
        <DialogContent className="sm:max-w-md" data-testid="note-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-amber-600" />
              Internal Notes
            </DialogTitle>
            <DialogDescription>
              Add private notes about this consultant (only visible to Super Admin)
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Add your notes here... (Markdown supported)"
              rows={6}
              className="resize-none"
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsNoteModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveNote}
              disabled={isProcessing}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              {isProcessing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* PURGE CONSULTANT MODAL */}
      <Dialog open={isPurgeOpen} onOpenChange={setIsPurgeOpen}>
        <DialogContent className="sm:max-w-md" data-testid="purge-consultant-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              Permanently Purge Consultant
            </DialogTitle>
            <DialogDescription>
              This will permanently delete{' '}
              <strong>{selectedConsultant?.full_name}</strong>{' '}
              ({selectedConsultant?.email}), their consultancy organization, all client links,
              and associated data. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="py-3">
            <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>
                The consultant&apos;s organization, all client-consultant links, and their profile
                data will be permanently removed.
              </span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPurgeOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handlePurgeConsultant}
              disabled={isProcessing}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-purge-consultant-btn"
            >
              {isProcessing
                ? <Loader2 className="w-4 h-4 animate-spin mr-2" />
                : <Trash2 className="w-4 h-4 mr-2" />}
              Purge Permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MANUAL ADD TRIAL REQUEST MODAL */}
      <Dialog open={isManualTrialOpen} onOpenChange={(open) => { if (!open) closeManualTrialModal(); }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="manual-trial-modal">
          {manualTrialResult ? (
            <div className="py-6 text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Trial Request Created
              </h3>
              <p className="text-slate-600 mb-4">
                A pending trial request was added. You can find it in the table below — click the row to approve or reject.
              </p>
              <Button onClick={closeManualTrialModal} className="bg-teal-700 hover:bg-teal-800 text-white">
                Done
              </Button>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-amber-600" />
                  Add Trial Request (Manual)
                </DialogTitle>
                <DialogDescription>
                  Manually create a pending trial request on behalf of someone who couldn't sign up themselves.
                  After creation, you can approve it to provision the workspace.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Request Type *</Label>
                  <Select
                    value={manualTrialForm.request_type}
                    onValueChange={(v) => setManualTrialForm({ ...manualTrialForm, request_type: v })}
                  >
                    <SelectTrigger data-testid="manual-trial-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CONSULTANCY">Consultancy</SelectItem>
                      <SelectItem value="CONTRACTOR">Contractor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Company Name *</Label>
                  <Input
                    value={manualTrialForm.company_name}
                    onChange={(e) => setManualTrialForm({ ...manualTrialForm, company_name: e.target.value })}
                    placeholder="Acme Federal Consulting"
                    data-testid="manual-trial-company"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>First Name *</Label>
                    <Input
                      value={manualTrialForm.contact_first_name}
                      onChange={(e) => setManualTrialForm({ ...manualTrialForm, contact_first_name: e.target.value })}
                      placeholder="Jane"
                      data-testid="manual-trial-first-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Last Name *</Label>
                    <Input
                      value={manualTrialForm.contact_last_name}
                      onChange={(e) => setManualTrialForm({ ...manualTrialForm, contact_last_name: e.target.value })}
                      placeholder="Doe"
                      data-testid="manual-trial-last-name"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={manualTrialForm.contact_email}
                    onChange={(e) => setManualTrialForm({ ...manualTrialForm, contact_email: e.target.value })}
                    placeholder="jane@acme-federal.com"
                    data-testid="manual-trial-email"
                  />
                  {manualTrialForm.contact_email &&
                   ['gmail.com','yahoo.com','hotmail.com','outlook.com','aol.com','icloud.com'].includes(
                     (manualTrialForm.contact_email.split('@')[1] || '').toLowerCase()
                   ) && (
                    <div className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
                      <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                      <span>Personal email domain — will be flagged but accepted.</span>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input
                      value={manualTrialForm.contact_phone}
                      onChange={(e) => setManualTrialForm({ ...manualTrialForm, contact_phone: e.target.value })}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Title</Label>
                    <Input
                      value={manualTrialForm.contact_title}
                      onChange={(e) => setManualTrialForm({ ...manualTrialForm, contact_title: e.target.value })}
                      placeholder="Managing Partner"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Plan</Label>
                  <Select
                    value={manualTrialForm.requested_plan}
                    onValueChange={(v) => setManualTrialForm({ ...manualTrialForm, requested_plan: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="STARTER">Starter</SelectItem>
                      <SelectItem value="GROWTH">Growth</SelectItem>
                      <SelectItem value="SCALE">Scale</SelectItem>
                      <SelectItem value="ENTERPRISE">Enterprise</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Internal Note</Label>
                  <Textarea
                    value={manualTrialForm.message}
                    onChange={(e) => setManualTrialForm({ ...manualTrialForm, message: e.target.value })}
                    placeholder="Why this is being added manually..."
                    rows={2}
                  />
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={closeManualTrialModal}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmitManualTrial}
                  disabled={
                    !manualTrialForm.company_name ||
                    !manualTrialForm.contact_first_name ||
                    !manualTrialForm.contact_last_name ||
                    !manualTrialForm.contact_email ||
                    isProcessing
                  }
                  className="bg-teal-700 hover:bg-teal-800 text-white"
                  data-testid="submit-manual-trial-btn"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4 mr-2" />
                      Create Trial Request
                    </>
                  )}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
